import { solveCaptcha } from "../blocks/Captcha/solveCaptcha";
import { gtmTrackEvent } from "../hooks/useGoogleTagManager";
import { pushError } from "../stores/hooks/useToasts";
import { reduxStore } from "../stores/reduxStore";
import { Token } from "../stores/slices/authSlice";
import { patch as patchCaptcha } from "../stores/slices/captchaSlice";
import { bugsnagNotify } from "../util/bugsnag";
import { getIsNationalAsParam } from "../util/getIsNationalAsParam";
import { clearSSOToken } from "../util/parseToken";
import { redactTokenId } from "../util/redactTokenId";
import { decodePayload } from "./decodePayload";
import { getApiBaseUrl } from "./getApiBase";
import { expireSession, upgradeToken, verifyToken } from "./upgradeToken";

export type FetcherResult<Data> = {
  message: string;
  payload: Data;
  success: boolean;
  token: Token;
};

const expressEndpoints = new Set(["/sign-in", "/sign-out"]);

// The retail security token travels to the API in this header (TW-1087, frontend
// audit finding 13/24), never as a query parameter, so it stays out of access logs,
// browser history and Referer headers. The API also accepts `Authorization: Bearer
// <tokenId>`. The form bodies below keep their tokenId field because Express's own
// /sign-in and /sign-out read it there. code/server/worker.js sends the same header.
const SECURITY_TOKEN_HEADER = "X-Security-Token";

const extend = (
  requestInfo: RequestInfo,
  requestInit?: RequestInit
): [string, RequestInit] => {
  const isNational = getIsNationalAsParam();
  const tokenId = reduxStore.getState().auth.tokenId;
  const searchToken = reduxStore.getState().captcha.token;

  const init = {
    ...requestInit,
    credentials: "same-origin" as RequestCredentials,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      // The token travels as a header, never in the URL (TW-1087). Form bodies keep
      // their tokenId field below: Express's /sign-in and /sign-out read it there.
      [SECURITY_TOKEN_HEADER]: tokenId,
      ...requestInit?.headers,
      // "X-Recaptcha-Token": captchToken || undefined,
    },
    method: requestInit?.method || "GET",
    referrerPolicy: "strict-origin" as ReferrerPolicy,
  };

  if (init.method !== "GET" && init.body instanceof URLSearchParams) {
    init.body.set("isNational", isNational);
    init.body.set("tokenId", tokenId);
    init.body.set("userIpAddress", window.ipAddress);
    searchToken && init.body.set("searchToken", searchToken);
  }

  const origin = expressEndpoints.has(String(requestInfo))
    ? window.location.origin
    : getApiBaseUrl();

  let apiURL = `${origin}${requestInfo}`;

  if (window.stripPhpExtension) {
    apiURL = apiURL.replace(".php", "");
  }

  if (
    init.method === "GET" ||
    init.headers["Content-Type"] === "application/json"
  ) {
    const ts = Date.now();
    const url = new URL(apiURL);
    url.searchParams.set("ts", ts.toString());
    url.searchParams.set("isNational", isNational);
    searchToken && url.searchParams.set("searchToken", searchToken);
    apiURL = url.toString();
  }

  return [apiURL, init];
};

// What a Bugsnag event may say about the failed call (TW-1144): never the headers
// (X-Security-Token is the shopper's bearer token) or the body (tokenId, passwords,
// customer details), only where it went and how it ended.
type RequestMetadata = {
  method: string;
  status: number;
  statusText: string;
  url: string;
};

const requestMetadata = (
  url: string,
  init: RequestInit,
  res: Response
): RequestMetadata => ({
  method: init.method ?? "GET",
  status: res.status,
  statusText: res.statusText,
  url,
});

export const fetcher = async <Data>(
  requestInfo: RequestInfo,
  requestInit?: RequestInit
): Promise<FetcherResult<Data>> => {
  await solveCaptcha(requestInfo);

  const tokenIsAuthenticated = reduxStore.getState().auth.tokenIsAuthenticated;
  let tokenId = reduxStore.getState().auth.tokenId;

  let [extendedRequestInfo, extendedRequestInit] = extend(
    requestInfo,
    requestInit
  );

  let res = await fetch(extendedRequestInfo, extendedRequestInit);

  if (res.status === 403 && tokenIsAuthenticated) {
    clearSSOToken();
    const token = await upgradeToken(tokenId);
    if (token) {
      tokenId = reduxStore.getState().auth.tokenId;
      [extendedRequestInfo, extendedRequestInit] = extend(
        requestInfo,
        requestInit
      );
      res = await fetch(extendedRequestInfo, extendedRequestInit);
      if (res.status === 403) {
        expireSession();
      }
    }
  }

  if (res.ok) {
    verifyToken(tokenId);
  }

  const redactedRequestInfo = redactTokenId(extendedRequestInfo);

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    if (body?.errorCode === "CAPTCHA_REQUIRED") {
      // The pass the backend held for this token is used up (TW-1089): drop the stored
      // response so the next submission solves the captcha again instead of failing twice.
      reduxStore.dispatch(patchCaptcha({ token: null }));
    }
    const error = new Error(
      body?.message ||
        body?.error ||
        `Failed to fetch: ${res.status} ${res.statusText}`
    );

    bugsnagNotify(error, (event) => {
      event.addMetadata(
        "apiRequest",
        requestMetadata(redactedRequestInfo, extendedRequestInit, res)
      );
    });
    pushError(error);
    gtmTrackEvent({
      event: extendedRequestInit.method ?? "GET",
      eventAction: redactedRequestInfo,
      eventCategory: "api-error",
      eventValue: error.message,
    });
    throw error;
  }

  const json = await res.json();
  const { message, payload, success, token } = json;

  if (!success && message) {
    const error = new Error(message);
    bugsnagNotify(error, (event) => {
      event.addMetadata(
        "apiRequest",
        requestMetadata(redactedRequestInfo, extendedRequestInit, res)
      );
    });
    pushError(error);
    gtmTrackEvent({
      event: extendedRequestInit.method ?? "GET",
      eventAction: redactedRequestInfo,
      eventCategory: "api-error",
      eventValue: error.message,
    });
    throw error;
  }

  gtmTrackEvent({
    event: extendedRequestInit.method ?? "GET",
    eventAction: redactedRequestInfo,
    eventCategory: "api",
    eventValue: message,
  });

  return {
    message,
    payload: decodePayload<Data>(payload ?? json),
    success,
    token,
  };
};

declare global {
  interface Window {
    stripPhpExtension: boolean;
  }
}
